import parseGiftcardToCartItem from '~/utils/parseGiftcardToCartItem'
import type {
  EventDate,
  EventDescription,
  Pricings,
} from '~/composables/types/event'
import type { GiftcardAddon, GiftcardCartItem } from '~/composables/types/cart'
import { useAlgoliaConversion } from '~/composables/algolia/useAlgoliaConversion'

interface AddGiftcardToCartParams {
  event: EventDescription
  pricings: Pricings
  giftcardType: 'VALUE' | 'EVENT' | 'SPECIAL'
  quantity?: number
  selectedTime?: EventDate
  /** Delivery option (PDF / CARD / BOX). Defaults to PDF inside the cart item parser. */
  giftcardAddon?: GiftcardAddon
  /** Embedded checkout only ever holds a single line, so the cart is reset first. */
  clearCartFirst?: boolean
}

/**
 * Shared "add this giftcard to the cart" flow: builds the cart item, applies the
 * chosen delivery option, opens the cart sidebar and reports the conversion.
 */
export const useGiftcardAddToCart = () => {
  const {
    addItem,
    clear: clearCart,
    toggleGiftcardAddon,
  } = useCartWithSubdomainLogic()
  const { toggleCartSidebar } = useUiState()
  const { sendAddToCartEvent } = useAlgoliaConversion()

  const addGiftcardToCart = async ({
    event,
    pricings,
    giftcardType,
    quantity = 1,
    selectedTime,
    giftcardAddon,
    clearCartFirst = false,
  }: AddGiftcardToCartParams): Promise<GiftcardCartItem | undefined> => {
    const cartItem = parseGiftcardToCartItem(
      event,
      quantity,
      ref(selectedTime),
      pricings,
      giftcardType
    )

    if (!cartItem) return undefined

    if (giftcardAddon) {
      cartItem.giftcardAddon = giftcardAddon
    }

    if (clearCartFirst) {
      clearCart()
    }

    await addItem(cartItem)

    // Re-adding a giftcard that is already in the cart merges the quantities and
    // keeps the existing line's delivery option, so re-apply the picked one.
    if (giftcardAddon) {
      toggleGiftcardAddon(cartItem, giftcardAddon)
    }

    toggleCartSidebar()
    sendAddToCartEvent(event)

    return cartItem
  }

  return { addGiftcardToCart }
}
